/**
 * Local edits the server hasn't acknowledged yet, kept in localStorage so they
 * survive closing the tab while offline, a crash, or a phone discarding the
 * page in the background. Every open doc writes under its own key, so two tabs
 * never overwrite each other; opening the doc again replays keys left behind.
 */
import * as Y from "yjs";

const PREFIX = "benleaf:outbox:";
/** Leftovers this old are dropped (their doc was probably deleted). */
const MAX_AGE_MS = 60 * 24 * 3600 * 1000;
const WRITE_DELAY_MS = 250;

const pageId = Math.random().toString(36).slice(2, 10);
let seq = 0;
const live = new Map<string, Outbox>();

interface Stored {
  /** Merged Yjs update, base64. */
  u: string;
  /** When the oldest unsaved edit was made. */
  t: number;
}

export class Outbox {
  private updates: Uint8Array[] = [];
  private since = 0;
  private written = false;
  private timer: number | undefined;
  private readonly key: string;

  constructor(projectId: string, docId: string) {
    this.key = `${PREFIX}${projectId}:${docId}:${pageId}.${++seq}`;
    live.set(this.key, this);
  }

  get pending(): boolean {
    return this.updates.length > 0;
  }

  add(update: Uint8Array) {
    if (!this.pending) this.since = Date.now();
    this.updates.push(update);
    this.timer ??= window.setTimeout(() => this.write(), WRITE_DELAY_MS);
  }

  /** Everything added so far reached the server. */
  clear() {
    clearTimeout(this.timer);
    this.timer = undefined;
    this.updates = [];
    if (this.written) remove(this.key);
    this.written = false;
  }

  write() {
    clearTimeout(this.timer);
    this.timer = undefined;
    if (!this.pending) return;
    const merged = this.updates.length > 1 ? Y.mergeUpdates(this.updates) : this.updates[0];
    this.updates = [merged];
    try {
      localStorage.setItem(this.key, JSON.stringify({ u: bytesToB64(merged), t: this.since } satisfies Stored));
      this.written = true;
    } catch {}
  }

  /** Stop tracking; whatever is still unsaved stays stored for the next visit. */
  dispose() {
    this.write();
    live.delete(this.key);
  }
}

// localStorage is synchronous, so writing here still lands while the page is going away.
function writeAll() {
  for (const o of live.values()) o.write();
}
window.addEventListener("pagehide", writeAll);
document.addEventListener("visibilitychange", () => {
  if (document.visibilityState === "hidden") writeAll();
});

export interface Leftover {
  key: string;
  raw: string;
  update: Uint8Array;
  since: number;
}

/** Docs of a project with edits left behind (by a closed tab, or a doc closed before it saved). */
export function leftoverDocs(projectId: string): string[] {
  const docs = new Set<string>();
  for (const { docId } of scan(projectId)) docs.add(docId);
  return [...docs];
}

export function leftovers(projectId: string, docId: string): Leftover[] {
  const out: Leftover[] = [];
  for (const e of scan(projectId)) {
    if (e.docId !== docId) continue;
    try {
      const stored = JSON.parse(e.raw) as Stored;
      out.push({ key: e.key, raw: e.raw, update: b64ToBytes(stored.u), since: stored.t });
    } catch {
      remove(e.key);
    }
  }
  return out;
}

/**
 * Forget leftovers once their edits are on the server. A key that changed in
 * the meantime belongs to another open tab that has added edits, so keep it.
 */
export function dropLeftovers(entries: Leftover[]) {
  for (const e of entries) {
    try {
      if (localStorage.getItem(e.key) === e.raw) localStorage.removeItem(e.key);
    } catch {}
  }
}

/** Forget every stored edit for a doc (it was deleted). */
export function dropDoc(projectId: string, docId: string) {
  for (const e of scan(projectId)) if (e.docId === docId) remove(e.key);
}

function scan(projectId: string): Array<{ key: string; docId: string; raw: string }> {
  const prefix = `${PREFIX}${projectId}:`;
  const out: Array<{ key: string; docId: string; raw: string }> = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix) || live.has(key)) continue;
      const raw = localStorage.getItem(key);
      if (raw === null) continue;
      out.push({ key, docId: key.slice(prefix.length).split(":")[0], raw });
    }
  } catch {
    return [];
  }
  const now = Date.now();
  return out.filter((e) => {
    try {
      if (now - (JSON.parse(e.raw) as Stored).t < MAX_AGE_MS) return true;
    } catch {}
    remove(e.key);
    return false;
  });
}

function remove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {}
}

function bytesToB64(bytes: Uint8Array): string {
  let s = "";
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}

function b64ToBytes(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}
